import React, { useState, useMemo, useRef, useEffect } from "react";
import { mapNodes, mapConnections } from "../data/knowledgeMapData";
import { getQuestionStats } from "../data/statsRepository";

const SECTION_TABS = [
  { key: "listening",        label: "聴解" },
  { key: "listeningReading", label: "聴読解" },
  { key: "reading",          label: "読解" },
];

const VIEWBOX_W = 520;
const VIEWBOX_H = 300;

// 做对题目数 / 题目总数 → 视觉状态
function nodeState(stats, questionIds) {
  const total = questionIds.length;
  if (total === 0) return "unvisited";

  const attempted = questionIds.filter(id => (stats[id]?.total ?? 0) > 0).length;
  if (attempted === 0) return "unvisited";

  const correctCount = questionIds.filter(id => (stats[id]?.correct ?? 0) > 0).length;
  const ratio = correctCount / total;

  if (ratio >= 1.0) return "mastered";
  if (ratio >= 0.7) return "good";
  if (ratio >= 0.4) return "mid";
  return "low";
}

// 状态 → 颜色 / glow
const STATE_STYLE = {
  unvisited: { fill: "#1e3a5f", stroke: "#2d4e73", glow: null,      r: 6 },
  low:       { fill: "#7f1d1d", stroke: "#ef4444", glow: "#ef4444", r: 7 },
  mid:       { fill: "#78350f", stroke: "#f59e0b", glow: "#f59e0b", r: 7 },
  good:      { fill: "#14532d", stroke: "#22c55e", glow: "#22c55e", r: 8 },
  mastered:  { fill: "#164e63", stroke: "#67e8f9", glow: "#67e8f9", r: 9 },
};

// 节点半径按题目数微调
function nodeRadius(base, count) {
  if (count >= 9) return base + 2;
  if (count >= 5) return base + 1;
  return base;
}

// 连线节点 id → 坐标
function buildNodeMap(nodes) {
  const m = {};
  nodes.forEach(n => { m[n.id] = { x: n.x, y: n.y }; });
  return m;
}

export default function KnowledgeMapView({ onNodeClick }) {
  const [activeSection, setActiveSection] = useState("listening");
  const [hoveredId, setHoveredId] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);

  const stats = useMemo(() => getQuestionStats(), []);

  const nodes = mapNodes[activeSection];
  const connections = mapConnections[activeSection];
  const nodeMap = useMemo(() => buildNodeMap(nodes), [nodes]);

  const hoveredNode = hoveredId ? nodes.find(n => n.id === hoveredId) : null;

  // 计算 hover 节点统计（做对题目数 / 题目总数）
  const hoveredStats = useMemo(() => {
    if (!hoveredNode) return null;
    const { questionIds } = hoveredNode;
    const attempted = questionIds.filter(id => (stats[id]?.total ?? 0) > 0).length;
    const correctQuestions = questionIds.filter(id => (stats[id]?.correct ?? 0) > 0).length;
    const ratio = questionIds.length > 0 ? Math.round(correctQuestions / questionIds.length * 100) : 0;
    return { attempted, correctQuestions, ratio, questionCount: questionIds.length };
  }, [hoveredNode, stats]);

  // SVG 坐标 → 页面坐标（用于 tooltip 定位）
  function handleMouseMove(e, node) {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const scaleX = rect.width  / VIEWBOX_W;
    const scaleY = rect.height / VIEWBOX_H;
    setTooltipPos({
      x: node.x * scaleX + rect.left,
      y: node.y * scaleY + rect.top,
    });
  }

  // section 切换时清除 hover
  useEffect(() => { setHoveredId(null); }, [activeSection]);

  return (
    <div className="flex flex-col flex-1 min-w-0">

      {/* Tab 栏 */}
      <div className="flex items-center gap-3 mb-4">
        {SECTION_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveSection(tab.key)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeSection === tab.key
                ? "bg-indigo-600 text-white"
                : "bg-gray-100 text-gray-500 hover:bg-gray-200"
            }`}
          >
            {tab.label}
          </button>
        ))}
        <span className="text-xs text-gray-400 ml-2">
          {nodes.length} 个知识节点 · {nodes.reduce((s, n) => s + n.questionIds.length, 0)} 题
        </span>
      </div>

      {/* 地图容器 */}
      <div className="relative flex-1 min-h-0 rounded-2xl overflow-hidden border border-gray-800">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
          className="w-full h-full"
          style={{ background: "#060e1a" }}
        >
          <defs>
            {/* 网格纹理 */}
            <pattern id="grid" width="24" height="24" patternUnits="userSpaceOnUse">
              <path d="M 24 0 L 0 0 0 24" fill="none" stroke="#0d1f33" strokeWidth="0.5" />
            </pattern>

            {/* 发光 filter — 复用单个 filter，通过颜色区分 */}
            <filter id="glow-sm" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="2.5" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <filter id="glow-md" x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <filter id="glow-lg" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="6" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

          {/* 背景 */}
          <rect width={VIEWBOX_W} height={VIEWBOX_H} fill="#060e1a" />
          <rect width={VIEWBOX_W} height={VIEWBOX_H} fill="url(#grid)" />

          {/* 连线 */}
          {connections.map(([fromId, toId], i) => {
            const from = nodeMap[fromId];
            const to   = nodeMap[toId];
            if (!from || !to) return null;
            // 贝塞尔曲线，让线条有弧度
            const mx = (from.x + to.x) / 2;
            const my = (from.y + to.y) / 2 - 18;
            return (
              <path
                key={i}
                d={`M ${from.x} ${from.y} Q ${mx} ${my} ${to.x} ${to.y}`}
                fill="none"
                stroke="#1e3a5f"
                strokeWidth="1"
                strokeDasharray="4 4"
                opacity="0.6"
              />
            );
          })}

          {/* 节点 */}
          {nodes.map(node => {
            const state  = nodeState(stats, node.questionIds);
            const style  = STATE_STYLE[state];
            const r      = nodeRadius(style.r, node.questionIds.length);
            const isHov  = hoveredId === node.id;
            const filter = state === "unvisited" ? null
                         : state === "mastered"  ? "url(#glow-lg)"
                         : isHov                 ? "url(#glow-md)"
                         :                         "url(#glow-sm)";

            return (
              <g key={node.id}>
                {/* 外光晕（仅已访问） */}
                {style.glow && (
                  <circle
                    cx={node.x} cy={node.y}
                    r={r + (isHov ? 6 : 3)}
                    fill={style.glow}
                    opacity={isHov ? 0.18 : 0.10}
                  />
                )}

                {/* 主圆 */}
                <circle
                  cx={node.x} cy={node.y} r={r}
                  fill={style.fill}
                  stroke={style.stroke}
                  strokeWidth={isHov ? 1.8 : 1.2}
                  filter={filter}
                  style={{ cursor: "pointer", transition: "r 0.2s" }}
                  onMouseEnter={e => { setHoveredId(node.id); handleMouseMove(e, node); }}
                  onMouseMove={e => handleMouseMove(e, node)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => onNodeClick?.(node)}
                />

                {/* 掌握标志：中心亮点 */}
                {state === "mastered" && (
                  <circle cx={node.x} cy={node.y} r={2.5} fill="#e0f2fe" opacity="0.9" />
                )}

                {/* 标签 */}
                <text
                  x={node.x}
                  y={node.y + r + 10}
                  textAnchor="middle"
                  fontSize="7.5"
                  fill={state === "unvisited" ? "#475569" : "#94a3b8"}
                  fontFamily="sans-serif"
                  style={{ pointerEvents: "none", userSelect: "none" }}
                >
                  {node.label}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Tooltip（绝对定位于页面） */}
        {hoveredNode && hoveredStats && (
          <NodeTooltip
            node={hoveredNode}
            stats={hoveredStats}
            pageX={tooltipPos.x}
            pageY={tooltipPos.y}
          />
        )}
      </div>

      {/* 图例 */}
      <div className="flex items-center gap-5 mt-3 px-1">
        {[
          { color: "#1e3a5f", label: "未接触" },
          { color: "#ef4444", label: "做对 <40%" },
          { color: "#f59e0b", label: "40–70%" },
          { color: "#22c55e", label: "70–99%" },
          { color: "#67e8f9", label: "全部做对" },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
            <span className="text-xs text-gray-500">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tooltip 组件 ──────────────────────────────────────

function NodeTooltip({ node, stats, pageX, pageY }) {
  const { attempted, correctQuestions, ratio, questionCount } = stats;
  const unattempted = questionCount - attempted;

  // 相对于 viewport 定位，防止超出右边界
  const left = Math.min(pageX + 12, window.innerWidth - 200);
  const top  = pageY - 8;

  return (
    <div
      className="fixed z-50 pointer-events-none"
      style={{ left, top }}
    >
      <div className="bg-gray-900 border border-gray-700 rounded-xl px-3 py-2.5 shadow-xl min-w-[168px]">
        <p className="text-xs font-semibold text-white mb-1.5">{node.label}</p>

        <div className="space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-gray-400">总题数</span>
            <span className="text-gray-200">{questionCount} 题</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-400">已尝试</span>
            <span className="text-gray-200">{attempted} 题</span>
          </div>
          {unattempted > 0 && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-400">未接触</span>
              <span className="text-amber-400">{unattempted} 题</span>
            </div>
          )}
          {attempted > 0 && (
            <>
              <div className="border-t border-gray-700 my-1" />
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">做对题目</span>
                <span className="text-gray-200">{correctQuestions} / {questionCount}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">进度</span>
                <span className={`font-semibold ${
                  ratio >= 70 ? "text-green-400" :
                  ratio >= 40 ? "text-amber-400" :
                  "text-red-400"
                }`}>{ratio}%</span>
              </div>
              <div className="w-full h-1.5 bg-gray-700 rounded-full mt-1 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${ratio}%`,
                    background: ratio >= 70 ? "#22c55e" : ratio >= 40 ? "#f59e0b" : "#ef4444",
                  }}
                />
              </div>
            </>
          )}
          <div className="border-t border-gray-700 mt-1.5 pt-1">
            <p className="text-xs text-indigo-400 text-center">单击进入练习 →</p>
          </div>
        </div>
      </div>
    </div>
  );
}
